/**
 * How this organisation writes an hour figure.
 *
 * Two conventions are in daily use in the same hangar. A Hobbs meter reads in
 * tenths — 1.5 h — and is the commonest source of block time on a light
 * aeroplane; a flight time logged for licensing is written 1:30. Neither is
 * more correct, so which one the screens use is an organisation setting, on
 * *Organisation info → Organisation details*.
 *
 * This is the browser's half of it. The server's half is
 * `apps/api/src/domain/hours-format.ts`, and the two have to produce the same
 * strings — somebody reads an aircraft's hours off a screen and then off the
 * certificate printed from it, and the two being written differently reads as a
 * fault in the records rather than as a setting. Change one, change both; the
 * strings are pinned by `hours-format.spec.ts`.
 *
 * ## What the setting does not reach
 *
 * **Limits, labour and rates.** A limit comes out of the manufacturer's manual
 * as a decimal — "100-hour inspection", "2 000 h TBO", "±10 h" — and writing it
 * "2000:00" misquotes the source. Man-hours get multiplied by a rate, where
 * "6:30 × €74.50" reads wrong on an invoice. A utilisation rate in minutes is
 * false precision. Those call `fmtDecimalHours`, which never follows the
 * setting, and they do so deliberately rather than by omission.
 *
 * **Storage.** Hours are decimal hours everywhere they are kept. This is a
 * convention for writing a number down, not a unit.
 */

export type HoursFormat = 'DECIMAL' | 'HOURS_MINUTES';

export const HOURS_FORMATS: readonly HoursFormat[] = ['DECIMAL', 'HOURS_MINUTES'];

export const HOURS_FORMAT_LABEL: Record<HoursFormat, string> = {
  DECIMAL: 'Decimal — 35.5 h',
  HOURS_MINUTES: 'Hours and minutes — 35:30',
};

const isHoursFormat = (v: unknown): v is HoursFormat =>
  typeof v === 'string' && (HOURS_FORMATS as readonly string[]).includes(v);

/**
 * The convention in force.
 *
 * Module state rather than something threaded through every render, because it
 * is read by roughly fifty call sites across six screens and passing it to each
 * would mean every one of them holding an organisation it otherwise has no use
 * for. It is set once from the session and again when somebody saves the
 * setting; an unrecognised value falls back to decimal rather than throwing,
 * because a screen that will not draw is worse than one drawn in tenths.
 */
let current: HoursFormat = 'DECIMAL';

export const setHoursFormat = (v: unknown): void => {
  current = isHoursFormat(v) ? v : 'DECIMAL';
};

export const hoursFormat = (): HoursFormat => current;

/** Decimal hours to one place, grouped: `3,411.9`. Never follows the setting. */
export const fmtDecimalHours = (h: number): string =>
  h.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Decimal hours as hours and minutes: `3,411:54`.
 *
 * Rounded to the nearest minute, and the carry is handled — 1.99999 h is 2:00,
 * not 1:60. The sign goes in front of the whole thing, because a margin can be
 * negative and "-1:30" is an hour and a half the wrong way.
 */
export function fmtHoursMinutes(h: number): string {
  const negative = h < 0;
  const total = Math.round(Math.abs(h) * 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${negative ? '-' : ''}${hours.toLocaleString('en-GB')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * An hour figure in the organisation's convention.
 *
 * For a **reading or a duration** — airframe hours, time since new or overhaul,
 * block time, what is left before something falls due, the point it falls due
 * at. For a limit, an interval, a tolerance, man-hours or a rate, call
 * `fmtDecimalHours`.
 *
 * The unit is not appended: call sites put ` h` after it, in both conventions.
 * "3,411:54 h" is not ambiguous, and dropping the unit in one mode and not the
 * other would be worse than keeping it in both.
 */
export const fmtHours = (h: number): string =>
  current === 'HOURS_MINUTES' ? fmtHoursMinutes(h) : fmtDecimalHours(h);

/**
 * Both conventions read back to a number of hours, whatever the setting says.
 *
 * Somebody who has set hours and minutes and types 1.5 out of habit means an
 * hour and a half, and reading it as one hour and five minutes would be a trap.
 * The reverse is just as true. There is no reading of either form that means
 * something else, so accepting both costs nothing.
 *
 * `1:75` is refused rather than read as 2:15 — that is somebody typing a
 * decimal into a colon field or the other way round, and guessing which would
 * put a figure nobody typed on an aircraft's counters.
 */
export function parseHours(raw: string): number | undefined {
  const text = raw.trim();
  if (!text) return undefined;

  const colon = /^(-?)(\d+):(\d{1,2})$/.exec(text);
  if (colon) {
    const minutes = Number(colon[3]);
    if (minutes > 59) return undefined;
    const value = Number(colon[2]) + minutes / 60;
    return colon[1] === '-' ? -value : value;
  }
  if (text.includes(':')) return undefined;

  const n = Number(text);
  return Number.isFinite(n) ? n : undefined;
}

/** What an hours box shows when it is empty. */
export const hoursPlaceholder = (): string => (current === 'HOURS_MINUTES' ? '0:00' : '0.0');

/**
 * The attributes an hours box carries.
 *
 * A box whose screen shows `3,411:54` and whose keypad offers only digits and a
 * dot is worse than no setting at all, so the input mode follows the convention
 * too — `text` under hours and minutes, because a colon is not on the decimal
 * keypad on a phone.
 */
export const hoursInputAttrs = (placeholder = hoursPlaceholder()): string =>
  `placeholder="${placeholder}" inputmode="${current === 'HOURS_MINUTES' ? 'text' : 'decimal'}"` +
  ' style="text-align:right;font-family:var(--mono)"';

/**
 * An hours figure prefilled into a box, written the way it will be read back.
 *
 * Without this an edit form shows a screen full of `3,411:54` above a box
 * containing `3411.9`, which invites somebody to "correct" the box to match the
 * screen and is exactly the round trip that has to stay lossless.
 */
export const hoursValue = (h: number | null | undefined): string => {
  if (h === null || h === undefined) return '';
  const n = Number(h);
  if (!Number.isFinite(n)) return '';
  return current === 'HOURS_MINUTES' ? fmtHoursMinutes(n).replace(/,/g, '') : String(n);
};
